import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, Button, Select } from "antd";
import { ReloadOutlined, ArrowRightOutlined } from "@ant-design/icons";
import {
  TbCurrencyDollar,
  TbShoppingCart,
  TbReceipt,
  TbDiscount,
  TbAlertTriangle,
  TbCreditCard,
  TbBuildingStore,
  TbTrendingUp,
  TbClock,
  TbAlertCircle,
} from "react-icons/tb";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { useRouterStore } from "@stores";
import { SubscriptionStatusEnum } from "@enums";
import "../../css/admin.css";

type DashPeriod = "today" | "week";

const COLORS = [
  "#08000a",
  "#E8320A",
  "#534AB7",
  "#854F0B",
  "#0C447C",
  "#A32D2D",
];

const PERIOD_OPTIONS: { value: DashPeriod; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "week", label: "Esta semana" },
];

// ── KPI card ─────────────────────────────────────────────
const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color,
  trend,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
  trend?: { value: number; label: string };
}) => (
  <div className="da-kpi-card">
    <div className="da-kpi-top">
      <div className="da-kpi-icon" style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      {trend && (
        <span className={`da-kpi-trend ${trend.value >= 0 ? "up" : "down"}`}>
          {trend.value >= 0 ? "↑" : "↓"} {Math.abs(trend.value)}%
        </span>
      )}
    </div>
    <span className="da-kpi-value">{value}</span>
    <span className="da-kpi-label">{label}</span>
    {sub && <span className="da-kpi-sub">{sub}</span>}
  </div>
);

// ── componente principal ──────────────────────────────────
const DashboardAdminComponent = () => {
  const navigate = useNavigate();
  const subscriptionStatusCode = useRouterStore(
    (s) => s.subscriptionStatusCode,
  );
  const daysLeft = useRouterStore((s) => s.daysLeft);
  const firstName = useRouterStore((s) => s.firstName);
  const [period, setPeriod] = useState<DashPeriod>("today");



  const fmt = (n?: number) =>
    `$${(n ?? 0).toLocaleString("es-EC", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtNum = (n?: number) => (n ?? 0).toLocaleString("es-EC");


  // ── skeleton ──────────────────────────────────────────
  const Skeleton = ({ w = "100%", h = 20 }: { w?: string; h?: number }) => (
    <div className="da-skeleton" style={{ width: w, height: h }} />
  );

  return (
    <div className="da-page">
      {subscriptionStatusCode === SubscriptionStatusEnum.ABOUT_TO_EXPIRE && (
        <div
          className={`da-sub-banner ${daysLeft !== null && daysLeft <= 2 ? "danger" : "warning"}`}
        >
          <div className="da-sub-banner-icon">
            {daysLeft !== null && daysLeft <= 2 ? (
              <TbAlertCircle size={18} />
            ) : (
              <TbClock size={18} />
            )}
          </div>
          <div className="da-sub-banner-text">
            {daysLeft === 0 ? (
              <>
                <strong>Tu suscripción vence hoy.</strong> Contacta a tu
                administrador para renovar y evitar perder el acceso.
              </>
            ) : daysLeft === 1 ? (
              <>
                <strong>Tu suscripción vence mañana.</strong> Contacta a tu
                administrador para renovar.
              </>
            ) : (
              <>
                <strong>Tu suscripción vence en {daysLeft} días.</strong>{" "}
                Comunícate con tu administrador para renovar a tiempo.
              </>
            )}
          </div>
          <div className="da-sub-banner-days">
            {daysLeft !== null && daysLeft >= 0 && (
              <span
                className={`da-sub-days-badge ${daysLeft <= 2 ? "danger" : "warning"}`}
              >
                {daysLeft === 0 ? "Vence hoy" : `${daysLeft}d restantes`}
              </span>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardAdminComponent;
